'use client';
import clsx from 'clsx';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import {
  PLAYER_PROFILE_DIVISION_TYPES,
  RANKING_HISTORY_STANDINGS_TYPES,
} from '@/modules/rankings/ppr/constants';

interface DivisionTypeButtonsWrapperProps {
  gender: string;
}

export const DivisionTypeButtonsWrapper = ({ gender }: DivisionTypeButtonsWrapperProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const initialDivision = searchParams?.get('division_type') ?? null;
  const pageType = searchParams?.get('type') || 'player-profile';
  const isRankHistory = pageType === 'rank-history';

  const RENDER_OPTIONS = isRankHistory
    ? RANKING_HISTORY_STANDINGS_TYPES
    : PLAYER_PROFILE_DIVISION_TYPES;

  const [selectedDivisionType, setSelectedDivisionType] = useState<string | null>(initialDivision);

  const handleClick = (divisionType: string | null) => {
    if (divisionType === selectedDivisionType) return;

    setSelectedDivisionType(divisionType);
    const newSearchParams = new URLSearchParams(searchParams || '');
    if (divisionType === null) {
      newSearchParams.delete('division_type');
    } else {
      newSearchParams.set('division_type', divisionType);
    }
    if (isRankHistory) {
      newSearchParams.delete('filter');
    }
    newSearchParams.set('page', '1');
    router.replace(`?${newSearchParams.toString()}`, { scroll: false });
  };

  useEffect(() => {
    const current = searchParams?.get('division_type') ?? null;
    setSelectedDivisionType(current);
  }, [searchParams, gender]);

  return (
    <>
      <div className='h-[56px] rounded-lg hidden sm:flex justify-center items-center gap-x-2 px-2 bg-brand-25'>
        {RENDER_OPTIONS.map((divisionType) => {
          const currentType = gender === 'Female' ? divisionType.femaleType : divisionType.maleType;
          const isSelected = currentType === selectedDivisionType;

          return (
            <button
              key={`division-type-${divisionType.id}`}
              className={clsx(
                'w-full h-11 rounded-lg flex items-center justify-center font-semibold',
                isSelected
                  ? 'bg-brand-700 text-white'
                  : 'text-gray-700 bg-background hover:bg-brand-100 hover:text-brand-800'
              )}
              onClick={() => handleClick(currentType)}
            >
              {divisionType.title}
            </button>
          );
        })}
      </div>
      <div className='h-[56px] rounded-lg sm:hidden overflow-x-scroll flex'>
        {RENDER_OPTIONS.map((divisionType) => {
          const currentType = gender === 'Female' ? divisionType.femaleType : divisionType.maleType;
          const isSelected = currentType === selectedDivisionType;

          return (
            <button
              key={`division-type-mobile-${divisionType.id}`}
              className={clsx(
                'flex-shrink-0 h-11 rounded-lg flex items-center justify-center font-semibold whitespace-nowrap px-6 mx-1',
                isSelected
                  ? 'bg-brand-700 text-white'
                  : 'text-gray-700 bg-background hover:bg-brand-100 hover:text-brand-800'
              )}
              onClick={() => handleClick(currentType)}
            >
              {divisionType.title}
            </button>
          );
        })}
      </div>
    </>
  );
};
